// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SectionWrapper from "@adl/App/Components/Common/SectionWrapper";
import { ellipsis } from "@adl/XDL/Utilities";
import { xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    selectedCategoryIds = "selectedCategoryIds",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.selectedCategoryIds]: string[];
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    categoriesDataView: getCategoriesDataView(state),
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PopularCategoriesStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    categoriesWrapper?: GridContainerProps;
    categoryGridItem?: GridItemProps;
    categoryWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    image?: LazyImageProps;
    imageWrapper?: InjectableCss;
}

export const popularCategoriesStyles: PopularCategoriesStyles = {
    centeringWrapper: {
        css: css`
            height: 215px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    categoriesWrapper: {
        gap: 22,
    },
    categoryGridItem: {
        width: [4, 4, 3, 2, 2],
    },
    categoryWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
        `,
    },
    link: {
        color: "text.main",
        css: css`
            font-size: 16px;
            display: block;
            font-weight: bold;
            text-align: center;
            line-height: 22px;
            ${xScreen("lte", "sm")} {
                font-size: 14px;
                font-weight: 600;
                line-height: 16px;
                ${ellipsis(3)}
            }
            ${ellipsis(2)}
        `,
    },
    image: {
        css: css`
            max-height: 100%;
            max-width: 100%;
            margin-bottom: 20px;
        `,
    },
    imageWrapper: {
        css: css`
            height: 158px;
            width: 158px;
            ${xScreen("lte", "sm")} {
                margin-bottom: 8px;
            }
            ${xScreen("lte", "xs")} {
                height: 92px;
                width: 92px;
            }
        `,
    },
};

const styles = popularCategoriesStyles;

const PopularCategoriesWidget: React.FC<Props> = ({
    fields: { selectedCategoryIds, title },
    loadCategories,
    categoriesDataView,
}) => {
    const [selectedCategories, setSelectedCategories] = React.useState<Category[]>([]);

    React.useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    }, []);

    React.useEffect(() => {
        const tempCategories = categoriesDataView.value?.filter(
            category => selectedCategoryIds.indexOf(category.id) > -1,
        );
        if (tempCategories?.length) {
            setSelectedCategories(tempCategories);
        }
    }, [selectedCategoryIds, categoriesDataView]);

    if (!selectedCategoryIds || selectedCategoryIds.length === 0) {
        return null;
    }

    if (categoriesDataView.isLoading || !categoriesDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper} data-test-selector="shopByCategory_Loading">
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    return (
        <SectionWrapper title={translate(title)}>
            <GridContainer {...styles.categoriesWrapper}>
                {selectedCategories?.map(category => (
                    <GridItem key={category.id} {...styles.categoryGridItem}>
                        <StyledWrapper {...styles.categoryWrapper}>
                            <Clickable href={category.path} {...styles.imageWrapper}>
                                <LazyImage
                                    src={category.smallImagePath}
                                    altText={category.shortDescription}
                                    {...styles.image}
                                    {...{
                                        transformations: { width: 158 },
                                    }}
                                    imgProps={{
                                        width: 158,
                                        height: 158,
                                    }}
                                />
                            </Clickable>
                            <Link href={category.path} {...styles.link} area-label={category.shortDescription}>
                                {category.shortDescription}
                            </Link>
                        </StyledWrapper>
                    </GridItem>
                ))}
            </GridContainer>
        </SectionWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PopularCategoriesWidget),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Popular Categories",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.selectedCategoryIds,
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                isRequired: true,
                validate: value => (value.length > 10 ? translate("You Can Add 10 Categories Only") : null),
            },
        ],
    },
};

export default widgetModule;
